import type React from "react";
import {
	Box,
	Button,
	Grid,
	Modal as MUIModal,
	styled,
	type BoxProps,
	type Breakpoint,
	type ButtonProps,
	type ModalProps as MUIModalProps,
	type SxProps,
} from "@mui/material";

import {
	ModalHeaderContainer,
	ModalContainer,
	ModalContentContainer,
	ModalTitleContainer,
	ModalCloseButton,
	ModalSubTitleContainer,
} from "../components";

const StyledFooterContainer = styled(Grid)(({ theme }) => ({
	backgroundColor: theme.darken(theme.palette.background.paper, 0.04),
	padding: theme.spacing(3),
}));

interface ConfirmationModalSlotProps {
	modalContainer?: BoxProps;
	contentContainer?: {
		sx?: SxProps;
	};
	confirmButton?: ButtonProps & {
		"data-testid"?: string;
		"data-pendoid"?: string;
	};
	cancelButton?: ButtonProps & {
		"data-testid"?: string;
		"data-pendoid"?: string;
	};
}

export interface ConfirmationModalProps extends Omit<MUIModalProps, "children" | "maxWidth" | "title"> {
	readonly children: React.ReactNode;
	readonly title: React.ReactNode | string;
	readonly subTitle?: React.ReactNode | string;
	readonly confirmText: string;
	readonly cancelText: string;
	readonly size?: Breakpoint;
	readonly actions?: React.ReactNode;
	readonly slotProps?: MUIModalProps["slotProps"] & ConfirmationModalSlotProps;
	onClose(e: React.UIEvent | object | undefined, reason?: "backdropClick" | "escapeKeyDown"): void;
	onConfirm(e: React.UIEvent | undefined): void | Promise<void>;
	onCancel(e: React.UIEvent | undefined): void | Promise<void>;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
	title,
	subTitle,
	confirmText,
	cancelText,
	children,
	size,
	sx,
	slotProps,
	actions,
	onConfirm,
	onCancel,
	...props
}) => {
	const { contentContainer, ...modalSlotProps } = slotProps || {};

	return (
		<MUIModal slotProps={modalSlotProps} {...props} role="dialog">
			<ModalContainer disableGutters maxWidth={size} sx={sx}>
				<Box
					{...slotProps?.modalContainer}
					sx={{ display: "flex", flexDirection: "column", overflow: "auto", ...slotProps?.modalContainer?.sx }}
				>
					<ModalHeaderContainer>
						<ModalTitleContainer>{title}</ModalTitleContainer>
						<ModalSubTitleContainer>{subTitle}</ModalSubTitleContainer>
						<ModalCloseButton onClick={props.onClose} />
					</ModalHeaderContainer>

					<ModalContentContainer sx={contentContainer?.sx}>{children}</ModalContentContainer>

					<StyledFooterContainer container justifyContent={actions ? "space-between" : "flex-end"}>
						{actions}

						<Grid container spacing={2}>
							<Button variant="text" onClick={onCancel} {...slotProps?.cancelButton}>
								{cancelText}
							</Button>
							<Button variant="contained" onClick={onConfirm} {...slotProps?.confirmButton}>
								{confirmText}
							</Button>
						</Grid>
					</StyledFooterContainer>
				</Box>
			</ModalContainer>
		</MUIModal>
	);
};
